import React, { useState } from "react";
import { api } from "../lib/api";
import AIAdBuilder from "./AIAdBuilder";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Search, Phone, Store, ArrowLeft, Sparkles, ShieldCheck, CheckCircle2, AlertTriangle, Info } from "lucide-react";
import { toast } from "sonner";

export default function AdminAdAssistant() {
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null); // null = sin buscar; [] = buscó y nada
  const [selected, setSelected] = useState(null); // { business, ad, subscription_active, ads_generated_this_month }

  const search = async (e) => {
    e?.preventDefault?.();
    const q = query.trim();
    if (q.length < 2) {
      return toast.error("Escribe al menos 2 caracteres (WhatsApp o nombre)");
    }
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/businesses/search?q=${encodeURIComponent(q)}`);
      setResults(data || []);
      if ((data || []).length === 0) {
        toast.info("Sin resultados. Verifica el número o el nombre.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al buscar");
    } finally {
      setBusy(false);
    }
  };

  const openBusiness = async (b) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/businesses/${b.id}/ad`);
      setSelected(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al abrir el comercio");
    } finally {
      setBusy(false);
    }
  };

  const refreshAd = (ad) => setSelected((prev) => ({ ...prev, ad }));

  const back = () => {
    setSelected(null);
  };

  // ===== Vista 1: buscador
  if (!selected) {
    return (
      <div className="space-y-5" data-testid="admin-ad-assistant">
        <HowToCard />

        <form onSubmit={search} className="bg-white border border-border rounded-2xl p-5">
          <label className="text-xs font-semibold text-zinc-700 uppercase tracking-wider">
            Buscar comercio por WhatsApp o nombre
          </label>
          <div className="mt-2 flex gap-2">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <Search className="w-4 h-4 text-zinc-400" />
              </div>
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Ej. 5512345678 o Tacos El Sabor"
                className="pl-9"
                data-testid="assist-search-input"
                autoFocus
              />
            </div>
            <Button type="submit" disabled={busy} className="btn-primary px-5" data-testid="assist-search-btn">
              {busy ? "Buscando…" : "Buscar"}
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">
            Tip: lo más rápido es buscar por los 10 dígitos del WhatsApp con el que el comercio se registró.
          </p>
        </form>

        {results !== null && (
          <div className="bg-white border border-border rounded-2xl">
            <div className="px-5 py-3 border-b border-border text-xs uppercase tracking-wider text-muted-foreground font-semibold">
              Resultados ({results.length})
            </div>
            {results.length === 0 ? (
              <div className="px-5 py-8 text-center text-muted-foreground text-sm">
                Sin coincidencias. Verifica el WhatsApp o intenta con el nombre del comercio.
              </div>
            ) : (
              <ul className="divide-y divide-border" data-testid="assist-results-list">
                {results.map((b) => (
                  <li key={b.id} className="px-5 py-4 flex items-center gap-4 hover:bg-zinc-50">
                    <div className="w-10 h-10 rounded-full bg-[#F5F3FF] border border-[#DDD6FE] grid place-items-center shrink-0">
                      <Store className="w-5 h-5 text-[#7C3AED]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-zinc-900 truncate">{b.business_name}</div>
                      <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                        <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{b.whatsapp}</span>
                        <span>{b.giro}</span>
                        <span>CP {b.postal_code}</span>
                        <span>{b.subscription_active ? <SubStatus active /> : <SubStatus />}</span>
                        {b.current_ad?.is_approved && <span className="text-emerald-700 font-semibold">✓ Anuncio aprobado</span>}
                        {b.current_ad && !b.current_ad.is_approved && <span className="text-amber-700 font-semibold">Borrador en curso</span>}
                      </div>
                    </div>
                    <Button size="sm" onClick={() => openBusiness(b)} disabled={busy} data-testid={`assist-open-${b.id}`}>
                      Diseñar
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    );
  }

  // ===== Vista 2: diseñador para el comercio elegido
  const { business: biz, ad, subscription_active } = selected;
  return (
    <div className="space-y-5" data-testid="admin-ad-assistant-builder">
      <button
        type="button"
        onClick={back}
        className="text-sm text-[#7C3AED] hover:underline inline-flex items-center gap-1"
        data-testid="assist-back-btn"
      >
        <ArrowLeft className="w-4 h-4" /> Volver a la búsqueda
      </button>

      <div className="bg-gradient-to-r from-[#F5F3FF] to-[#EDE9FE] border border-[#DDD6FE] rounded-2xl px-5 py-4 flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-white border border-border grid place-items-center shrink-0">
          <Sparkles className="w-6 h-6 text-[#7C3AED]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-[#7C3AED] font-semibold">Asistiendo a</div>
          <div className="font-display text-xl text-zinc-900 truncate">{biz.business_name}</div>
          <div className="text-xs text-zinc-600 mt-0.5 flex flex-wrap gap-x-3">
            <span><Phone className="w-3 h-3 inline mr-0.5" />{biz.whatsapp}</span>
            <span>{biz.giro}</span>
            <span>CP {biz.postal_code}</span>
          </div>
        </div>
        <div className="text-right text-xs">
          <div className={subscription_active ? "text-emerald-700 font-semibold flex items-center gap-1" : "text-rose-700 font-semibold flex items-center gap-1"}>
            {subscription_active ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
            {subscription_active ? "Suscripción activa" : "Suscripción inactiva"}
          </div>
        </div>
      </div>

      {!subscription_active && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 text-sm text-rose-900 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            Este comercio no tiene suscripción activa. Para generar un anuncio primero aprueba su pago de $199 MXN en la pestaña "Pagos".
          </span>
        </div>
      )}

      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-900 flex items-start gap-2">
        <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
        <span>
          <b>Modo asistente activo.</b> Estás generando el anuncio EN NOMBRE del comercio. Aplican las mismas reglas (aprobación bloquea el diseño, límite de 4 generaciones IA por ciclo).
        </span>
      </div>

      <AIAdBuilder
        business={biz}
        ad={ad}
        onAdSaved={refreshAd}
        adminTargetBusinessId={biz.id}
      />
    </div>
  );
}

function SubStatus({ active = false }) {
  return (
    <span className={`inline-flex items-center gap-1 ${active ? "text-emerald-700" : "text-rose-700"} font-semibold`}>
      <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-emerald-500" : "bg-rose-500"}`} />
      {active ? "Activo" : "Inactivo"}
    </span>
  );
}

function HowToCard() {
  return (
    <div className="bg-white border border-border rounded-2xl p-5" data-testid="assist-howto">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#F5F3FF] grid place-items-center shrink-0">
          <Info className="w-5 h-5 text-[#7C3AED]" />
        </div>
        <div className="flex-1">
          <h3 className="font-display text-xl tracking-tight">Asistente de diseño para comercios</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Ayuda a un comercio a diseñar su anuncio con la IA cuando ellos no puedan hacerlo solos. Aplican las mismas reglas que en el dashboard del comercio.
          </p>
          <ol className="mt-3 space-y-1.5 text-sm text-zinc-700 list-decimal list-inside">
            <li><b>Busca el comercio</b> por su número de WhatsApp (10 dígitos) o por nombre.</li>
            <li>Selecciónalo y haz clic en <b>"Diseñar"</b>.</li>
            <li>Captura el <b>producto</b> y la <b>promoción</b>, elige estilo y colores → <b>"Generar mi anuncio"</b>.</li>
            <li>Refina con instrucciones cortas si el diseño necesita ajustes ("agranda el 2x1", "fondo rojo", etc.).</li>
            <li>Cuando el comercio apruebe → <b>"Aprobar diseño definitivo del mes"</b>. ¡Listo, queda publicado en el directorio!</li>
          </ol>
          <div className="mt-3 text-[11px] text-muted-foreground bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2">
            🛡️ Importante: cada generación se registra en el log con tu identidad de admin. Solo úsalo con autorización del comercio.
          </div>
        </div>
      </div>
    </div>
  );
}
